import { useEffect, useMemo, useState } from "react";
import {
  api,
  type PipelineHistoryEntry,
  type PipelineRecentRow,
  type PipelineState,
  type PipelineStateResponse,
} from "../api/client";

// ---------------------------------------------------------------------------
// Stage model — labels, colors, order used for the timeline strip.
// ---------------------------------------------------------------------------

const STAGE_ORDER: PipelineState[] = [
  "pending",
  "classifying",
  "planning",
  "executing",
  "reviewing",
  "revising",
  "deploying",
  "completed",
];

const STAGE_LABEL: Record<PipelineState, string> = {
  pending: "Очередь",
  classifying: "Классификатор",
  planning: "Планер",
  executing: "Исполнение",
  reviewing: "Ревью",
  revising: "Доработка",
  deploying: "Деплой",
  completed: "Готово",
  escalated: "Эскалация",
};

const STAGE_ICON: Record<PipelineState, string> = {
  pending: "⏸",
  classifying: "🏷",
  planning: "🧠",
  executing: "✍",
  reviewing: "🔍",
  revising: "🔧",
  deploying: "🚀",
  completed: "✅",
  escalated: "⚠️",
};

const STAGE_AGENT: Record<PipelineState, string> = {
  pending: "—",
  classifying: "детерминистика",
  planning: "Opus 4.7",
  executing: "Claude (IDE)",
  reviewing: "Codex GPT-5.4",
  revising: "Claude (IDE)",
  deploying: "gitea API",
  completed: "—",
  escalated: "—",
};

const STAGE_DOING: Record<PipelineState, string> = {
  pending: "ждёт очереди",
  classifying: "классифицирует задачу",
  planning: "строит план",
  executing: "пишет код",
  reviewing: "проверяет tsc+tests+lint",
  revising: "исправляет замечания",
  deploying: "мержит ветку",
  completed: "всё готово",
  escalated: "требует внимания",
};

const ACTIVE_STATES: Set<PipelineState> = new Set([
  "classifying",
  "planning",
  "executing",
  "reviewing",
  "revising",
  "deploying",
]);

const TERMINAL_STATES: Set<PipelineState> = new Set(["completed", "escalated"]);

function isActive(state: PipelineState | null): boolean {
  return state !== null && ACTIVE_STATES.has(state);
}

function isDone(state: PipelineState | null): boolean {
  return state !== null && TERMINAL_STATES.has(state);
}

// ---------------------------------------------------------------------------
// Time utils.
// ---------------------------------------------------------------------------

function formatDuration(fromIso: string | null, toIso: string | null): string {
  if (!fromIso || !toIso) return "—";
  const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  if (ms < 1000) return `${ms}мс`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}с`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return `${m}м ${rem}с`;
  const h = Math.floor(m / 60);
  return `${h}ч ${m % 60}м`;
}

function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function elapsedSince(iso: string | null | undefined, now: number): string {
  if (!iso) return "—";
  const ms = now - new Date(iso).getTime();
  if (ms < 0) return "—";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}с`;
  const m = Math.floor(s / 60);
  return `${m}м ${s % 60}с`;
}

// ---------------------------------------------------------------------------
// Pipeline row (one per task).
// ---------------------------------------------------------------------------

interface RowMeta {
  duration_ms?: number;
  cost_cents?: number;
  consensus?: boolean;
  verdict?: string;
  revision_count?: number;
  branch?: string;
  tier?: number;
  reason?: string;
}

function readMeta(entry: PipelineHistoryEntry): RowMeta {
  return (entry.meta as RowMeta | undefined) ?? {};
}

/** Animated dots — visible "сейчас работает" signal. */
function RunningDots() {
  return (
    <span className="inline-flex gap-0.5" aria-label="работает">
      <span
        className="inline-block h-1 w-1 rounded-full bg-sym-teal animate-sym-pulse"
        style={{ animationDelay: "0ms" }}
      />
      <span
        className="inline-block h-1 w-1 rounded-full bg-sym-teal animate-sym-pulse"
        style={{ animationDelay: "200ms" }}
      />
      <span
        className="inline-block h-1 w-1 rounded-full bg-sym-teal animate-sym-pulse"
        style={{ animationDelay: "400ms" }}
      />
    </span>
  );
}

function PipelineTimeline({ detail }: { detail: PipelineStateResponse }) {
  const history = detail.history ?? [];
  const current = detail.state;
  const latestByStage = new Map<PipelineState, PipelineHistoryEntry>();
  history.forEach((h) => {
    latestByStage.set(h.to, h);
  });

  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
      {STAGE_ORDER.filter((s) => s !== "pending" && s !== "completed").map((stage) => {
        const entered = latestByStage.get(stage);
        const isActiveStage = current === stage;
        const isDoneStage = history.some((h) => h.from === stage);
        const nextEntry = history.find((h) => h.from === stage);
        const duration =
          entered && nextEntry ? formatDuration(entered.at, nextEntry.at) : "—";
        const nextMeta = nextEntry ? readMeta(nextEntry) : {};

        let dotClass = "sym-dot-idle";
        let labelEl: React.ReactNode = <span className="text-sym-muted">ожидает</span>;
        let iconOpacity = 0.35;
        if (isActiveStage) {
          dotClass = "sym-dot-running";
          labelEl = (
            <span className="text-sym-teal inline-flex items-center gap-1">
              работает <RunningDots />
            </span>
          );
          iconOpacity = 1;
        } else if (isDoneStage) {
          dotClass = "sym-dot-done";
          labelEl = <span className="text-sym-teal">готово</span>;
          iconOpacity = 0.85;
        }
        if (current === "escalated" && isActiveStage) {
          dotClass = "sym-dot-err";
          labelEl = <span className="text-sym-danger">сбой</span>;
          iconOpacity = 1;
        }

        const summary: string[] = [];
        if (stage === "planning" && nextMeta.cost_cents !== undefined) {
          summary.push(`${nextMeta.cost_cents}¢`);
          if (nextMeta.consensus === false) summary.push("архитектор");
        }
        if (stage === "executing" && nextMeta.branch) {
          summary.push(`ветка ${nextMeta.branch.slice(-8)}`);
        }
        if (stage === "reviewing" && nextMeta.verdict) {
          summary.push(nextMeta.verdict);
        }
        if (stage === "revising" && nextMeta.revision_count !== undefined) {
          summary.push(`r${nextMeta.revision_count}`);
        }

        return (
          <div
            key={stage}
            className={`sym-card ${isActiveStage ? "relative overflow-hidden" : ""}`}
            style={{
              borderColor: isActiveStage
                ? "rgba(16, 163, 127, 0.55)"
                : isDoneStage
                  ? "rgba(16, 163, 127, 0.2)"
                  : undefined,
              boxShadow: isActiveStage
                ? "inset 0 0 24px rgba(16, 163, 127, 0.08)"
                : undefined,
            }}
          >
            {isActiveStage && (
              <div className="pointer-events-none absolute inset-0 sym-shimmer opacity-50" />
            )}
            <div className="relative flex items-center gap-2">
              <span className={`sym-dot ${dotClass}`} />
              <span
                className="text-xl leading-none select-none"
                style={{ opacity: iconOpacity, filter: !isActiveStage && !isDoneStage ? "grayscale(100%)" : undefined }}
                aria-hidden
              >
                {STAGE_ICON[stage]}
              </span>
              <span className="text-sm font-semibold text-panel-fg">{STAGE_LABEL[stage]}</span>
              <span className="ml-auto text-xs tabular">{labelEl}</span>
            </div>
            <div className="relative mt-1 text-xs text-panel-muted">{STAGE_AGENT[stage]}</div>
            {isActiveStage && (
              <div className="relative mt-0.5 text-xs italic text-sym-teal">
                {STAGE_DOING[stage]}…
              </div>
            )}
            <div className="relative mt-2 flex items-center justify-between text-xs">
              <span className="text-panel-muted tabular">{duration}</span>
              <span className="text-sym-teal tabular">{summary.join(" · ")}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function HistoryTrail({ history }: { history: PipelineHistoryEntry[] }) {
  if (history.length === 0) return null;
  return (
    <div className="mt-3 border-t border-sym-line pt-3">
      <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-panel-muted">
        История переходов
      </div>
      <ul className="space-y-0.5 font-mono text-xs tabular">
        {history.map((h, i) => {
          const meta = readMeta(h);
          const parts: string[] = [];
          if (meta.cost_cents !== undefined) parts.push(`${meta.cost_cents}¢`);
          if (meta.duration_ms !== undefined) parts.push(`${Math.round(meta.duration_ms / 1000)}с`);
          if (meta.verdict) parts.push(meta.verdict);
          if (meta.revision_count !== undefined) parts.push(`r${meta.revision_count}`);
          if (meta.reason) parts.push(meta.reason);
          return (
            <li key={i} className="flex gap-2">
              <span className="text-panel-muted">{formatTime(h.at)}</span>
              <span className="text-panel-muted">{h.from ?? "∅"}</span>
              <span className="text-panel-muted">→</span>
              <span className={h.to === "escalated" ? "text-sym-danger" : "text-panel-fg"}>
                {h.to}
              </span>
              {parts.length > 0 && (
                <span className="ml-auto text-sym-teal">{parts.join(" · ")}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PipelineCard({ row, now }: { row: PipelineRecentRow; now: number }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState<PipelineStateResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!expanded) return;
    let cancelled = false;
    const fetchIt = async () => {
      try {
        setLoading(true);
        const res = await api.pipeline.state(row.id);
        if (!cancelled) {
          setDetail(res);
          setErr(null);
        }
      } catch (e) {
        if (!cancelled) setErr(String(e).slice(0, 200));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchIt();
    // auto-refresh while expanded AND state is still active
    const activeState = isActive(detail?.state ?? row.state);
    const interval = activeState ? 5000 : 20000;
    const t = setInterval(fetchIt, interval);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [expanded, row.id, detail?.state, row.state]);

  const state = detail?.state ?? row.state;
  const active = isActive(state);
  const done = isDone(state);
  const escalated = state === "escalated";

  let dotClass = "sym-dot-idle";
  if (active) dotClass = "sym-dot-running";
  else if (escalated) dotClass = "sym-dot-err";
  else if (done) dotClass = "sym-dot-done";

  const stateLabel = state ? STAGE_LABEL[state] : "—";
  const agent = state ? STAGE_AGENT[state] : "—";
  const icon = state ? STAGE_ICON[state] : "·";
  const doing = state ? STAGE_DOING[state] : "";
  const lastUpdate = detail?.updated_at ?? row.updated_at;
  const elapsed = active ? elapsedSince(lastUpdate ?? row.started_at, now) : null;
  // Stall detection: active state + last update > 3 min ago = likely stuck.
  const isStalled =
    active &&
    lastUpdate !== null &&
    lastUpdate !== undefined &&
    now - new Date(lastUpdate).getTime() > 3 * 60_000;

  const historyCount = detail?.history?.length ?? 0;
  const firstEntry = detail?.history?.[0];
  const totalDuration =
    firstEntry && (done || active)
      ? formatDuration(firstEntry.at, detail?.updated_at ?? new Date(now).toISOString())
      : null;

  return (
    <div
      className={`sym-card sym-card-hover cursor-pointer ${active ? "relative overflow-hidden" : ""}`}
      onClick={() => setExpanded((v) => !v)}
      style={{
        borderColor: isStalled
          ? "rgba(234, 179, 8, 0.55)"
          : active
            ? "rgba(16, 163, 127, 0.5)"
            : escalated
              ? "rgba(180, 35, 24, 0.4)"
              : undefined,
      }}
    >
      {active && !isStalled && (
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[2px] sym-shimmer" />
      )}
      <div className="relative flex items-start gap-3">
        <span
          className="mt-0.5 text-2xl leading-none select-none"
          style={{ filter: !active && !done && !escalated ? "grayscale(100%)" : undefined }}
          aria-hidden
        >
          {icon}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-panel-fg">{row.title}</span>
            {row.tier !== null && (
              <span className="shrink-0 rounded border border-sym-line px-1.5 py-0.5 text-[10px] font-mono text-panel-muted">
                T{row.tier}
              </span>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-2">
            <span className={`sym-dot ${dotClass}`} />
            <span
              className={`text-sm font-semibold ${
                escalated ? "text-sym-danger" : active ? "text-sym-teal" : "text-panel-fg"
              }`}
            >
              {stateLabel}
            </span>
            {active && !isStalled && (
              <span className="inline-flex items-center gap-1 text-xs text-sym-teal">
                · работает <RunningDots />
              </span>
            )}
            {active && isStalled && (
              <span className="inline-flex items-center gap-1 text-xs text-panel-warn">
                · ⚠ застрял {elapsed}
              </span>
            )}
            {done && !escalated && (
              <span className="text-xs text-sym-teal">· завершено</span>
            )}
          </div>
          {active && (
            <div className="mt-1 flex items-center gap-2 text-xs">
              <span className="tabular text-panel-fg">{agent}</span>
              {doing && <span className="italic text-sym-teal">— {doing}…</span>}
            </div>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-panel-muted">
            {!active && <span className="tabular">{agent}</span>}
            {elapsed && active && <span className="tabular">на этапе {elapsed}</span>}
            {totalDuration && <span className="tabular">· всего {totalDuration}</span>}
            {row.verdict && <span className="font-mono uppercase">· {row.verdict}</span>}
            {row.escalation_reason && (
              <span className="text-sym-danger font-mono">· {row.escalation_reason}</span>
            )}
          </div>
          <div className="mt-0.5 font-mono text-[11px] text-panel-muted">
            {row.id.slice(0, 8)} · обновлено {formatTime(row.updated_at)}
          </div>
        </div>
        <span className="shrink-0 text-xs text-panel-muted">
          {expanded ? "▲" : "▼"} {historyCount > 0 ? `${historyCount} шагов` : ""}
        </span>
      </div>

      {expanded && (
        <div className="relative mt-4">
          {loading && !detail && <div className="text-xs text-panel-muted">Загружаю…</div>}
          {err && <div className="text-xs text-sym-danger font-mono">{err}</div>}
          {detail && (
            <>
              <PipelineTimeline detail={detail} />
              <HistoryTrail history={detail.history} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page-level: lanes (Running / Escalated / Recent done) + live clock.
// ---------------------------------------------------------------------------

interface Lanes {
  running: PipelineRecentRow[];
  escalated: PipelineRecentRow[];
  completed: PipelineRecentRow[];
}

function splitLanes(items: PipelineRecentRow[]): Lanes {
  const running: PipelineRecentRow[] = [];
  const escalated: PipelineRecentRow[] = [];
  const completed: PipelineRecentRow[] = [];
  for (const it of items) {
    if (it.state === "completed") completed.push(it);
    else if (it.state === "escalated") escalated.push(it);
    else running.push(it);
  }
  return { running, escalated, completed };
}

export function Pipeline() {
  const [items, setItems] = useState<PipelineRecentRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  const [lastRefresh, setLastRefresh] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await api.pipeline.recent(40);
        if (!cancelled) {
          setItems(res.items);
          setLastRefresh(Date.now());
          setErr(null);
        }
      } catch (e) {
        if (!cancelled) setErr(String(e).slice(0, 200));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    tick();
    const t = setInterval(tick, 7_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const lanes = useMemo(() => splitLanes(items), [items]);

  const totalCost = useMemo(() => {
    // best-effort: sum cost_cents from latest history entries — requires detail fetch
    // for now display count only; cost per task is visible on expand
    return items.length;
  }, [items]);

  return (
    <div className="sym-page min-h-full">
      <div className="mx-auto max-w-6xl px-4 py-6">
        {/* Hero */}
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-panel-fg">Pipeline</h1>
            <p className="mt-1 text-sm text-panel-muted">
              Реал-тайм автопилота: классификатор → планер → executor → reviewer → деплой
            </p>
          </div>
          <div className="flex items-center gap-3 text-xs text-panel-muted tabular">
            <span className="inline-flex items-center gap-1">
              <span className="sym-dot sym-dot-running" />
              <span>{lanes.running.length} идёт</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="sym-dot sym-dot-err" />
              <span>{lanes.escalated.length} эскалация</span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="sym-dot sym-dot-done" />
              <span>{lanes.completed.length} готово</span>
            </span>
            <span className="border-l border-sym-line pl-3">
              последняя обновка{" "}
              {lastRefresh ? new Date(lastRefresh).toLocaleTimeString("ru-RU") : "—"}
            </span>
          </div>
        </div>

        {loading && <div className="text-sm text-panel-muted">Загружаю пайплайны…</div>}
        {err && <div className="mb-4 text-sm text-sym-danger font-mono">{err}</div>}

        {!loading && items.length === 0 && (
          <div className="sym-card text-sm text-panel-muted">
            Пока ни одна задача не прошла pipeline. Стартни через{" "}
            <code className="font-mono">POST /api/pipeline/start</code> или со страницы «Задачи».
          </div>
        )}

        {/* Running swimlane */}
        {lanes.running.length > 0 && (
          <section className="mb-6">
            <div className="mb-2 flex items-baseline gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-panel-fg">
                Сейчас идёт
              </h2>
              <span className="text-xs text-panel-muted tabular">{lanes.running.length}</span>
            </div>
            <div className="space-y-2">
              {lanes.running.map((r) => (
                <PipelineCard key={r.id} row={r} now={now} />
              ))}
            </div>
          </section>
        )}

        {/* Escalated swimlane */}
        {lanes.escalated.length > 0 && (
          <section className="mb-6">
            <div className="mb-2 flex items-baseline gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-sym-danger">
                Эскалация
              </h2>
              <span className="text-xs text-panel-muted tabular">{lanes.escalated.length}</span>
            </div>
            <div className="space-y-2">
              {lanes.escalated.slice(0, 10).map((r) => (
                <PipelineCard key={r.id} row={r} now={now} />
              ))}
            </div>
          </section>
        )}

        {/* Completed swimlane */}
        {lanes.completed.length > 0 && (
          <section className="mb-6">
            <div className="mb-2 flex items-baseline gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-panel-fg">
                Недавно завершено
              </h2>
              <span className="text-xs text-panel-muted tabular">{lanes.completed.length}</span>
            </div>
            <div className="space-y-2">
              {lanes.completed.slice(0, 10).map((r) => (
                <PipelineCard key={r.id} row={r} now={now} />
              ))}
            </div>
          </section>
        )}

        <div className="mt-8 border-t border-sym-line pt-4 text-center text-xs text-panel-muted tabular">
          refresh 7с · {totalCost} задач в окне
        </div>
      </div>
    </div>
  );
}
